import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Text from 'ol/style/Text';
import Icon from 'ol/style/Icon';

// ┌────────────────────────┬────────────────────────────────┐
// │ لایه                   │ نوع نمایش                      │
// ├────────────────────────┼────────────────────────────────┤
// │ traffic                │ خط رنگی بر اساس ترافیک       │
// │ water / waterway       │ آبی (خط و سطح)                │
// │ building               │ خاکستری (سطح)                 │
// │ transportation         │ جاده‌های رنگی                  │
// │ place                  │ نام شهر‌ها و روستاها           │
// │ poi                    │ نقاط مهم (دایره + نام)        │
// │ park                   │ سبز (مناطق حفاظت‌شده)          │
// │ boundary               │ خط قرمز (مرزها)                │
// │ mountain_peak          │ قله کوه‌ها                      │
// │ aeroway                │ فرودگاه‌ها                      │
// │ landuse                │ کاربری زمین                   │
// │ landcover              │ پوشش زمین                     │
// │ parcels                │ پلاک‌های شهری (زمین/قطعه)      │
// │ plaques                │ پلاک‌های نصب شده                │
// └────────────────────────┴────────────────────────────────┘


// ┌─────────────┬────────────────────────────────────────────┐
// │ زوم         │ توضیحات                                   │
// ├─────────────┼────────────────────────────────────────────┤
// │ 0-5         │ کشور قاره                                  │
// │ 6-9         │ استان منطقه                                │
// │ 10-12       │ شهرستان شهر بزرگ                          │
// │ 13-15       │ محله‌ها خیابان اصلی                        │
// │ 16-18       │ خیابان‌های فرعی پارک‌ها                   │
// │ 19-20       │ جزئیات ساختمان پلاک‌ها                    │
// └─────────────┴────────────────────────────────────────────┘


// ============================================
// رنگ‌های ترافیک
// ============================================
export const TRAFFIC_COLORS: { [key: string]: string } = {
    low: '#2ECC71',           // 🟢 سبز - ترافیک کم
    moderate: '#F39C12',     // 🟠 نارنجی - ترافیک متوسط
    heavy: '#E74C3C',       // 🔴 قرمز - ترافیک سنگین
    severe: '#8E44AD',     // 🟣 بنفش - ترافیک بسیار شدید
    closed: '#2C3E50',    // ⚫ مشکی - بسته شده
    default: '#95A5A6'   // ⚪ خاکستری - نامشخص
};

// ============================================
// ضخامت جاده‌ها
// ============================================
export const ROAD_WIDTHS: { [key: string]: number } = {
    motorway: 6,                // آزادراه
    motorway_link: 4,          // آزادراه (رابط)
    trunk: 5,                 // بزرگر
    trunk_link: 3,           // بزرگر (رابط)
    primary: 4,             // جاده اصلی
    primary_link: 3,       // جاده اصلی (رابط)
    tertiary: 3,          // جاده فرعی
    residential: 2,      // جاده مسکونی
    default: 1          // پیش‌فرض
};

// ============================================
// رنگ‌های لایه‌های مختلف
// ============================================
export const LAYER_COLORS: { [key: string]: any } = {
    // آب
    water: {
        fill: '#4ECDC4',
        stroke: '#26A69A'
    },
    // رودخانه
    waterway: {
        stroke: '#4ECDC4'
    },
    // پوشش زمین
    landcover: {
        fill: '#D4A373'
    },
    // کاربری زمین
    landuse: {
        fill: '#9B59B6',
        stroke: '#8E44AD'
    },
    // مرز
    boundary: {
        stroke: '#E74C3C'
    },
    // حمل‌ونقل (جاده‌ها)
    transportation: {
        stroke: '#3498DB'
    },
    // ساختمان
    building: {
        fill: '#95A5A6',
        stroke: '#7F8C8D'
    },
    // نام جاده‌ها
    transportation_name: {
        stroke: '#2980B9'
    },
    // مکان‌ها
    place: {
        fill: '#E67E22'
    },
    // نقاط مهم
    poi: {
        fill: '#E74C3C'
    },
    // فرودگاه
    aeroway: {
        fill: '#BDC3C7',
        stroke: '#95A5A6'
    },
    // نام فرودگاه
    aerodrome_label: {
        fill: '#2C3E50'
    },
    // نام آب‌ها
    water_name: {
        fill: '#1ABC9C'
    },
    // شماره پلاک
    housenumber: {
        fill: '#7F8C8D'
    },
    // پارک
    park: {
        fill: '#27AE60',
        stroke: '#1E8449'
    },
    // قله
    mountain_peak: {
        fill: '#8B4513'
    }
};

// ============================================
// تنظیمات استایل خط
// ============================================
export const STROKE_OPTIONS = {
    lineCap: 'round' as const,
    lineJoin: 'round' as const
};

// ============================================
// توابع کمکی
// ============================================

export function getTrafficColor(congestion: string | undefined): string {
    if (!congestion) return TRAFFIC_COLORS.default;
    return TRAFFIC_COLORS[congestion] || TRAFFIC_COLORS.default;
}

export function getRoadWidth(roadClass: string | undefined): number {
    if (!roadClass) return ROAD_WIDTHS.default;
    return ROAD_WIDTHS[roadClass] || ROAD_WIDTHS.default;
}

export function getLayerColor(layerName: string): any {
    return LAYER_COLORS[layerName] || { fill: '#95A5A6', stroke: '#7F8C8D' };
}

// ============================================
// استایل ترافیک
// ============================================
export function getTrafficStyle(feature: any): Style {
    const congestion = feature.get('congestion');
    const roadClass = feature?.get('class') ?? feature?.get('road_class');

    const color = getTrafficColor(congestion);
    const width = getRoadWidth(roadClass);

    return new Style({
        stroke: new Stroke({
            color: color,
            width: width,
            ...STROKE_OPTIONS
        })
    });
}

// ============================================
// استایل آب (lake, river)
// ============================================
export function getWaterStyle(feature: any, resolution: number): Style {
    const layerName = feature.get('layer');
    const classType = feature.get('class');

    if (layerName === 'waterway') {
        // رودخانه - خط
        return new Style({
            stroke: new Stroke({
                color: '#4ECDC4',
                width: 3,
                ...STROKE_OPTIONS
            })
        });
    }

    // دریاچه - سطح
    return new Style({
        fill: new Fill({
            color: 'rgba(78, 205, 196, 0.3)'
        }),
        stroke: new Stroke({
            color: '#26A69A',
            width: 1
        })
    });
}

// ============================================
// استایل ساختمان
// ============================================
export function getBuildingStyle(feature: any): Style {
    return new Style({
        fill: new Fill({
            color: 'rgba(149, 165, 166, 0.5)'
        }),
        stroke: new Stroke({
            color: '#7F8C8D',
            width: 0.5
        })
    });
}

// ============================================
// استایل جاده‌ها (transportation)
// ============================================
export function getTransportationStyle(feature: any): Style {
    const roadClass = feature.get('class');
    const width = getRoadWidth(roadClass);

    // رنگ بر اساس نوع جاده
    let color = '#3498DB';

    switch (roadClass) {
        case 'motorway':
            color = '#E74C3C';
            break;
        case 'trunk':
            color = '#F39C12';
            break;
        case 'primary':
            color = '#F1C40F';
            break;
        case 'secondary':
            color = '#2ECC71';
            break;
        case 'tertiary':
            color = '#1ABC9C';
            break;
        case 'residential':
            color = '#BDC3C7';
            break;
        default:
            color = '#95A5A6';
    }

    return new Style({
        stroke: new Stroke({
            color: color,
            width: width,
            ...STROKE_OPTIONS
        })
    });
}



// ============================================
// استایل مکان‌ها (شهرها، روستاها) - با کنترل زوم
// ============================================
export function getPlaceStyle(feature: any, resolution: number): Style {

    const classType = feature.get('class');
    const name = feature.get('name');
    const rank = feature.get('rank') || 10;

    // محاسبه زوم
    const zoom = getZoomFromResolution(resolution);

    let minZoom = 8;
    switch (classType) {
        case 'city':
            minZoom = 6;
            break;
        case 'town':
            minZoom = 9;
            break;
        case 'village':
            minZoom = 12;
            break;
        case 'suburb':
            minZoom = 14;
            break;
        case 'neighbourhood':
            minZoom = 15;
            break;
        case 'quarter':
            minZoom = 16;
            break;
        case 'hamlet':
            minZoom = 13;
            break;
        case 'locality':
            minZoom = 11;
            break;
    }

    if (zoom < minZoom) {
        return new Style({});
    }

    // اندازه فونت بر اساس زوم
    let fontSize = 10;
    if (rank <= 2) fontSize = 18;
    else if (rank <= 4) fontSize = 14;
    else if (rank <= 6) fontSize = 12;
    else if (rank <= 8) fontSize = 11;

    // رنگ بر اساس نوع
    let color = '#2C3E50';
    if (classType === 'city') color = '#E74C3C';
    else if (classType === 'town') color = '#E67E22';
    else if (classType === 'village') color = '#3498DB';

    return new Style({
        text: new Text({
            text: name || '',
            font: `${fontSize}px Vazirmatn`,
            fill: new Fill({ color: color }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            textAlign: 'center',
            textBaseline: 'bottom'
        })
    });
}


// ============================================
// استایل POI (نقاط مهم) - با کنترل زوم
// ============================================
export function getPoiStyle(feature: any, resolution: number): Style {
    const classType = feature.get('class');
    const name = feature.get('name');
    const zoomLevel = feature.get('zoom_level');

    const zoom = getZoomFromResolution(resolution);
    // console.log(`zoom: ${zoom}`, `mostShow: ${zoomLevel}`)

    // حداقل زوم برای نمایش
    const minZoom = zoomLevel || 12;

    if (zoom < minZoom) {
        return new Style({});
    }

    // رنگ بر اساس نوع
    let color = '#E74C3C';

    switch (classType) {
        case 'hospital':
            color = '#E74C3C';
            break;
        case 'school':
            color = '#F39C12';
            break;
        case 'railway':
            color = '#9B59B6';
            break;
        case 'restaurant':
            color = '#E67E22';
            break;
        case 'fuel':
            color = '#2ECC71';
            break;
        case 'parking':
            color = '#3498DB';
            break;
        default:
            color = '#95A5A6';
    }

    // فقط در زوم بالا نام رو نشون بده
    const showLabel = zoom >= 16;

    return new Style({
        image: new Circle({
            radius: zoom >= 14 ? 5 : 3,
            fill: new Fill({ color: color }),
            stroke: new Stroke({ color: 'white', width: 1 })
        }),
        text: showLabel && name ? new Text({
            text: name,
            font: '10px Vazirmatn',
            fill: new Fill({ color: '#2C3E50' }),
            stroke: new Stroke({ color: 'white', width: 1.5 }),
            offsetY: -8
        }) : undefined
    });
}

// ============================================
// استایل پارک و مناطق حفاظت‌شده
// ============================================
export function getParkStyle(feature: any): Style {
    const classType = feature.get('class');

    let fillColor = 'rgba(39, 174, 96, 0.3)';
    let strokeColor = '#1E8449';

    if (classType === 'protected_area') {
        fillColor = 'rgba(142, 68, 173, 0.2)';
        strokeColor = '#8E44AD';
    }

    return new Style({
        fill: new Fill({ color: fillColor }),
        stroke: new Stroke({ color: strokeColor, width: 1 })
    });
}

// ============================================
// استایل مرزها
// ============================================
export function getBoundaryStyle(feature: any): Style {
    const disputed = feature.get('disputed');
    const adminLevel = feature.get('admin_level');

    let color = '#E74C3C';
    let width = 1;

    if (adminLevel === 2) {
        // مرز کشوری
        color = '#C0392B';
        width = 2;
    } else if (adminLevel <= 4) {
        color = '#E74C3C';
        width = 1.5;
    }

    if (disputed === 1) {
        color = '#F39C12'; // مرزهای مورد اختلاف
    }

    return new Style({
        stroke: new Stroke({
            color: color,
            width: width,
            lineDash: adminLevel === 2 ? undefined : [4, 4]
        })
    });
}

// ============================================
// استایل قله کوه
// ============================================
export function getMountainPeakStyle(feature: any, resolution: number): Style {
    const zoom = getZoomFromResolution(resolution);

    // از زوم ۱۰ به بالا نشون بده
    if (zoom < 10) {
        return new Style({});
    }

    const name = feature.get('name');
    const ele = feature.get('ele');
    const rank = feature.get('rank') || 10;

    // رتبه‌های پایین = قله‌های مهم‌تر
    let minRank = 8;
    if (zoom < 12) minRank = 3;
    else if (zoom < 14) minRank = 5;

    if (rank > minRank) {
        return new Style({});
    }

    return new Style({
        image: new Circle({
            radius: zoom >= 14 ? 4 : 3,
            fill: new Fill({ color: '#8B4513' }),
            stroke: new Stroke({ color: 'white', width: 1.5 })
        }),
        text: new Text({
            text: name ? `${name}${ele ? ` (${ele}m)` : ''}` : '',
            font: '10px Vazirmatn',
            fill: new Fill({ color: '#5D4037' }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            offsetY: -8
        })
    });
}

// ============================================
// استایل فرودگاه
// ============================================
export function getAerowayStyle(feature: any): Style {
    const classType = feature.get('class');

    return new Style({
        fill: new Fill({ color: 'rgba(189, 195, 199, 0.5)' }),
        stroke: new Stroke({ color: '#95A5A6', width: 1 })
    });
}

// ============================================
// استایل نام فرودگاه
// ============================================
export function getAerodromeLabelStyle(feature: any): Style {
    const name = feature.get('name');
    const iata = feature.get('iata');

    return new Style({
        text: new Text({
            text: name || '',
            font: 'bold 11px Vazirmatn',
            fill: new Fill({ color: '#2C3E50' }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            textAlign: 'center',
            offsetY: -10
        }),
        image: iata ? new Circle({
            radius: 3,
            fill: new Fill({ color: '#2C3E50' })
        }) : undefined
    });
}

// ============================================
// استایل کاربری زمین
// ============================================
export function getLanduseStyle(feature: any): Style {
    const classType = feature.get('class');

    let fillColor = 'rgba(155, 89, 182, 0.2)';

    switch (classType) {
        case 'military':
            fillColor = 'rgba(231, 76, 60, 0.2)';
            break;
        case 'residential':
            fillColor = 'rgba(236, 240, 241, 0.3)';
            break;
        case 'commercial':
            fillColor = 'rgba(241, 196, 15, 0.2)';
            break;
        case 'industrial':
            fillColor = 'rgba(149, 165, 166, 0.2)';
            break;
        case 'forest':
            fillColor = 'rgba(39, 174, 96, 0.2)';
            break;
        case 'farmland':
            fillColor = 'rgba(241, 196, 15, 0.2)';
            break;
    }

    return new Style({
        fill: new Fill({ color: fillColor })
    });
}

// ============================================
// استایل پوشش زمین
// ============================================
export function getLandcoverStyle(feature: any): Style {
    const classType = feature.get('class');

    let fillColor = '#D4A373';

    switch (classType) {
        case 'sand':
            fillColor = '#D4A373';
            break;
        case 'rock':
            fillColor = '#95A5A6';
            break;
        case 'snow':
            fillColor = '#ECF0F1';
            break;
        case 'wood':
            fillColor = 'rgba(39, 174, 96, 0.3)';
            break;
    }

    return new Style({
        fill: new Fill({ color: fillColor })
    });
}

// ============================================
// استایل پلاک‌ها - فقط در زوم بالا
// ============================================
export function getParcelStyle(feature: any, resolution: number): Style {
    const zoom = getZoomFromResolution(resolution);

    // فقط از زوم ۱۶ به بالا نشون بده
    if (zoom < 16) {
        return new Style({});
    }

    const plateNameFa = feature.get('plate_name_fa');
    const plateNameEn = feature.get('plate_name_en');

    return new Style({
        fill: new Fill({
            color: 'rgba(255, 193, 7, 0.3)'
        }),
        stroke: new Stroke({
            color: '#FFC107',
            width: 2
        }),
        text: new Text({
            text: plateNameFa || plateNameEn || '',
            font: 'bold 12px Vazirmatn',
            fill: new Fill({ color: '#5D4037' }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            textAlign: 'center',
            textBaseline: 'middle',
            padding: [2, 2, 2, 2]
        })
    });
}

// ============================================
// استایل پلاک‌های نصب شده (plaques)
// ============================================
export function getPlaqueStyle(feature: any, resolution: number): Style {
    const zoom = getZoomFromResolution(resolution);

    // فقط از زوم ۱۷ به بالا نشون بده
    if (zoom < 17) {
        return new Style({});
    }

    const plateNameFa = feature.get('plate_name_fa');
    const plateNameEn = feature.get('plate_name_en');

    return new Style({
        image: new Circle({
            radius: 6,
            fill: new Fill({ color: '#FF5722' }),
            stroke: new Stroke({ color: 'white', width: 2 })
        }),
        text: new Text({
            text: plateNameFa || plateNameEn || '',
            font: 'bold 10px Vazirmatn',
            fill: new Fill({ color: '#E64A19' }),
            stroke: new Stroke({ color: 'white', width: 1.5 }),
            textAlign: 'center',
            textBaseline: 'bottom',
            offsetY: -8,
            padding: [1, 1, 1, 1]
        })
    });
}

// ============================================
// تابع اصلی - استایل همه لایه‌ها
// ============================================
export function getVectorTileStyle(feature: any, resolution: number): Style | undefined {
    const layerName = feature.get('layer');

    if (!layerName) {
        return getDefaultStyle(feature);
    }

    switch (layerName) {
        case 'traffic':
            return getTrafficStyle(feature);

        case 'water':
        case 'waterway':
        case 'water_name':
            return getWaterStyle(feature, resolution);

        case 'building':
            return getBuildingStyle(feature);

        case 'transportation':
        case 'transportation_name':
            return getTransportationStyle(feature);

        case 'place':
            return getPlaceStyle(feature, resolution);

        case 'poi':
            return getPoiStyle(feature, resolution);

        case 'park':
            return getParkStyle(feature);

        case 'boundary':
            return getBoundaryStyle(feature);

        case 'mountain_peak':
            return getMountainPeakStyle(feature, resolution);

        case 'aeroway':
        case 'aerodrome_label':
            return getAerowayStyle(feature);

        case 'landuse':
            return getLanduseStyle(feature);

        case 'landcover':
            return getLandcoverStyle(feature);

        case 'parcels':
            return getParcelStyle(feature, resolution);

        case 'plaques':
            return getPlaqueStyle(feature, resolution);

        case 'housenumber':
            return getDefaultStyle(feature);

        default:
            return getDefaultStyle(feature);
    }
}
// ============================================
// استایل پیش‌فرض
// ============================================
export function getDefaultStyle(feature: any): Style {
    return new Style({
        stroke: new Stroke({
            color: '#3498DB',
            width: 1,
            ...STROKE_OPTIONS
        }),
        fill: new Fill({
            color: 'rgba(52, 152, 219, 0.1)'
        })
    });
}


// تابع کمکی برای محاسبه زوم از رزولوشن
// ============================================
// ============================================
export function getZoomFromResolution(resolution: number): number {
    return Math.round(Math.log2(156543.03392 / resolution));
}